// components/StatsigWrapper.tsx
import { useEffect, useState, useRef, ReactNode } from "react";
import Cookies from "js-cookie";
import { StatsigProvider, StatsigUser, StatsigClient } from "@statsig/react-bindings";
import { PublicSettings } from "@/src/config/PublicSettings";
import { StatsigUtils } from "@/src/utils/StatsigUtils";
import { Utils } from "@/src/Utils";
import { useQuoteData } from "../hooks/useQuoteData";

interface StatsigWrapperProps {
    children: ReactNode;
}

export function StatsigWrapper({ children }: StatsigWrapperProps) {
    const clientRef = useRef<StatsigClient | null>(null);
    const [client, setClient] = useState<StatsigClient | null>(null);
    const [user, setUser] = useState<StatsigUser>({
        userID: "",
        custom: { isNewTUID: false }
    });

    const { quoteId } = useQuoteData();

    // Step 1 — Determine user
    useEffect(() => {
        const tuidCookieValue = Cookies.get("tuid");
        const allQueryParams = Utils.getQueryParameters();

        const statsigUser = StatsigUtils.getStatsigUser({
            searchParams: allQueryParams,
            cookieID: tuidCookieValue,
            userID: user.userID,
            initialCustom: { hasQuoteIdCookie: !!quoteId }
        });
        const setStatsigUser = () => setUser(statsigUser);
        setStatsigUser();
    }, [quoteId, user.userID]);

    // Step 2 — Initialize Statsig client after user is set
    useEffect(() => {
        if (typeof window === "undefined" || !PublicSettings.STATSIG_CLIENT_KEY || !user.userID) {
            return;
        }

        if (clientRef.current) {
            setClient(clientRef.current);
            return;
        }

        const inst = new StatsigClient(PublicSettings.STATSIG_CLIENT_KEY, user, {
            environment: { tier: PublicSettings.ENVIRONMENT }
        });

        inst.initializeAsync();
        clientRef.current = inst;
        setClient(inst);
    }, [user]);

    if (!user.userID) {
        return <>{children}</>;
    }

    if (!PublicSettings.STATSIG_CLIENT_KEY || !client) {
        return <>{children}</>;
    }

    return <StatsigProvider client={client}>{children}</StatsigProvider>;
}
